import { Drawer, Segmented, Space, Typography, Upload, Button, message, Image, Modal } from 'antd';
import dayjs from '../config/dayjs';
import { Device } from '../types/device.types';
import { useState, useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DeviceContractInfo } from './device-contract-info';
import { DeviceInstallments } from './device-installments';
import { UploadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from '../config/axios';
import { AuthContext } from '../context/auth.context';

interface DeviceDetailsProps {
	device: Device;
	onClose: () => void;
}

const { Text } = Typography;

// Agents add pictures from the app, so what was loaded with the page can be out of
// date. Every change starts from the device as it is now.
const loadDevice = async (id: number): Promise<Device> => {
	const response = await axios.get(`/admin/devices/${id}`, { withCredentials: true });
	return response.data.results;
};

// DeviceImages component for uploading device pictures and sale receipt
const DeviceImages = ({ device }: { device: Device }) => {
	const { user } = useContext(AuthContext);
	const queryClient = useQueryClient();
	const [devicePictures, setDevicePictures] = useState<string[]>(device.device_pictures || []);
	const [saleReceipt, setSaleReceipt] = useState<string>(device.sale_receipt || '');
	const [loading, setLoading] = useState(false);

	const isSuperAdmin = user?.is_superuser || user?.admin_type === 'super_admin';

	const show = (current: Device) => {
		setDevicePictures(current.device_pictures || []);
		setSaleReceipt(current.sale_receipt || '');
	};

	useEffect(() => {
		loadDevice(device.id)
			.then(show)
			.catch(() => undefined);
	}, [device.id]);

	const handleUpload = async (file: File, type: 'device' | 'receipt') => {
		setLoading(true);
		const formData = new FormData();
		formData.append('file', file);

		try {
			const response = await axios.post('/fundamentals/file-upload', formData, { withCredentials: true });
			const url = response.data?.results?.secure_url;

			if (url) {
				const current = await loadDevice(device.id);
				// A receipt that arrived since the page opened is kept, and this one
				// joins the pictures, as the agent app does with a second receipt.
				const changes =
					type === 'receipt' && !current.sale_receipt
						? { sale_receipt: url }
						: { device_pictures: [...(current.device_pictures || []), url] };
				await axios.patch(`/admin/devices/edit/${device.id}`, changes, { withCredentials: true });
				show({ ...current, ...changes });
				queryClient.invalidateQueries({ queryKey: ['devices'] });
				message.success(type === 'device' ? 'Device picture uploaded successfully' : 'Sale receipt uploaded successfully');
			}
		} catch (error) {
			console.error('Upload failed:', error);
			message.error('Failed to upload image');
		} finally {
			setLoading(false);
		}
	};

	const handleDevicePictureRemove = (picture: string) => {
		Modal.confirm({
			title: 'Remove this image?',
			content: 'This will permanently delete the image from the device record.',
			okText: 'Remove',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				try {
					const current = await loadDevice(device.id);
					const newPictures = (current.device_pictures || []).filter((pic) => pic !== picture);
					await axios.patch(
						`/admin/devices/edit/${device.id}`,
						{ device_pictures: newPictures },
						{ withCredentials: true },
					);
					show({ ...current, device_pictures: newPictures });
					queryClient.invalidateQueries({ queryKey: ['devices'] });
					message.success('Image removed successfully');
				} catch (error) {
					console.error('Failed to update device pictures:', error);
					message.error('Failed to remove image');
				}
			},
		});
	};

	return (
		<div className="space-y-8">
			<div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
				<h3 className="mb-5 text-lg font-semibold text-gray-800">Device Pictures</h3>
				<Image.PreviewGroup>
					<div className="mb-4 grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4">
						{devicePictures.map((pic, index) => (
							<div
								key={index}
								className="flex flex-col space-y-2"
							>
								<div className="overflow-hidden rounded-lg shadow-sm transition-all hover:shadow-md">
									<Image
										src={pic}
										alt={`Device ${index + 1}`}
										className="h-48 w-full cursor-pointer object-cover"
										style={{ objectFit: 'cover' }}
										preview={{
											mask: <div className="text-white">Click to preview</div>,
										}}
									/>
								</div>
								{isSuperAdmin && (
									<Button
										danger
										type="primary"
										size="small"
										icon={<DeleteOutlined />}
										onClick={() => handleDevicePictureRemove(pic)}
										className="w-full"
									>
										Remove
									</Button>
								)}
							</div>
						))}
						<Upload
							beforeUpload={(file) => {
								handleUpload(file, 'device');
								return false; // Prevent default upload behavior
							}}
							showUploadList={false}
							disabled={loading}
						>
							<div className="flex h-48 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-all duration-300 hover:border-blue-500 hover:bg-gray-100">
								<div className="px-4 py-3 text-center">
									<PlusOutlined className="text-2xl text-gray-500" />
									<div className="mt-3 text-gray-600">Add Device Photo</div>
								</div>
							</div>
						</Upload>
					</div>
				</Image.PreviewGroup>
			</div>

			<div className="rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
				<h3 className="mb-5 text-lg font-semibold text-gray-800">Sale Receipt</h3>
				{saleReceipt ? (
					<div className="mb-4">
						<div className="mb-4 max-w-2xl overflow-hidden rounded-lg border border-gray-200 shadow-sm">
							<Image
								src={saleReceipt}
								alt="Sale Receipt"
								className="w-full cursor-pointer"
								preview={{
									mask: <div className="text-white">Click to preview</div>,
								}}
							/>
						</div>
						{isSuperAdmin && (
							<Button
								danger
								type="primary"
								icon={<DeleteOutlined />}
								className="mt-2"
								onClick={() => {
									Modal.confirm({
										title: 'Remove sale receipt?',
										content: 'This will permanently delete the receipt from the device record.',
										okText: 'Remove',
										okType: 'danger',
										cancelText: 'Cancel',
										onOk: async () => {
											try {
												setSaleReceipt('');
												await axios.patch(`/admin/devices/edit/${device.id}`, { sale_receipt: '' }, { withCredentials: true });
												queryClient.invalidateQueries({ queryKey: ['devices'] });
												message.success('Receipt removed successfully');
											} catch (error) {
												setSaleReceipt(saleReceipt);
												console.error('Failed to remove receipt:', error);
												message.error('Failed to remove receipt');
											}
										},
									});
								}}
							>
								Remove Receipt
							</Button>
						)}
					</div>
				) : (
					<Upload
						beforeUpload={(file) => {
							handleUpload(file, 'receipt');
							return false; // Prevent default upload behavior
						}}
						showUploadList={false}
						disabled={loading}
					>
						<div className="flex h-32 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-all duration-300 hover:border-blue-500 hover:bg-gray-100">
							<div className="px-6 py-4 text-center">
								<UploadOutlined className="text-2xl text-gray-500" />
								<div className="mt-3 text-gray-600">Upload Sale Receipt</div>
							</div>
						</div>
					</Upload>
				)}
			</div>
		</div>
	);
};

const DeviceDetails = ({ device, onClose }: DeviceDetailsProps) => {
	const [selectedTab, setSelectedTab] = useState<'Device Contract Info' | 'Installment Details' | 'Device Images'>('Device Contract Info');

	if (!device) {
		return <div>No device found</div>;
	}

	return (
		<Drawer
			open={true}
			onClose={onClose}
			title="Device & Contract Details"
			width={'80%'}
			footer={
				<div className="flex justify-end">
					<Space>
						<Text type="secondary">Contract created on {dayjs(device.created_at).format('DD MMM YYYY')}</Text>
					</Space>
				</div>
			}
		>
			<Segmented
				options={['Device Contract Info', 'Installment Details', 'Device Images']}
				onChange={(value) => {
					console.log(value);
					setSelectedTab(value as 'Device Contract Info' | 'Installment Details' | 'Device Images');
				}}
				className="mb-4"
				color="red"
			/>

			{selectedTab === 'Device Contract Info' && <DeviceContractInfo device={device} />}
			{selectedTab === 'Installment Details' && <DeviceInstallments device={device} />}
			{selectedTab === 'Device Images' && <DeviceImages device={device} />}
		</Drawer>
	);
};

export default DeviceDetails;
